// Shared per-body date-range picker. iOS-Health-style: segmented
// granularity tabs (W/M/6M/Y/All) and a paging row (« ← label → »).
//
// The label is locked to a fixed width so the row keeps a stable height
// across paging. Anything that resized the picker also resized the chart
// area below it via flex-1 — and a height change at the chart wrapper
// flipped recharts' updateId mid-flight, killing the Pie animation.
//
// «/» jump to the earliest/latest data-bearing window. ←/→ step to the
// adjacent data-bearing window (empty windows are skipped). All four
// disable in lockstep with canPrev/canNext.

import React from 'react';
import {
  type Granularity, type TimeRangeState,
  GRANULARITIES, windowLabel, isAllTime,
} from './windowRange';

interface Props {
  state: TimeRangeState;
  /** Which granularity tabs to render. Defaults to all five (`GRANULARITIES`).
   *  Callers can narrow it per context — e.g. a `period_over_period` summary
   *  card passes `['W','M','Y']` to drop the meaningless 6M/All tabs. Only the
   *  rendered tabs change; paging and window math are unaffected. */
  granularities?: readonly Granularity[];
}

const ChartTimeRangePicker: React.FC<Props> = ({ state, granularities = GRANULARITIES }) => {
  const {
    granularity, anchor, setRange,
    pagePrev, pageNext, goEarliest, goLatest,
    canPrev, canNext,
  } = state;
  const allTime = isAllTime(granularity);
  const prevDisabled = allTime || !canPrev;
  const nextDisabled = allTime || !canNext;
  const pickGranularity = (g: Granularity) => setRange(g, anchor);

  const arrowBtn = (onClick: () => void, disabled: boolean, title: string, d: string) => (
    <button
      onClick={() => { if (!disabled) onClick(); }}
      disabled={disabled}
      className="p-1 rounded-md hover:bg-gray-100 transition-colors disabled:hover:bg-transparent"
      title={title}
    >
      <svg
        className={`w-4 h-4 ${disabled ? 'text-gray-300' : 'text-gray-600'}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
      </svg>
    </button>
  );

  return (
    <div className="mb-2 px-1">
      <div className="flex items-center justify-between flex-wrap gap-1.5">
        <div className="flex items-center min-w-0">
          {arrowBtn(goEarliest, prevDisabled, 'Earliest', 'M18.75 19.5L11.25 12l7.5-7.5M11.25 19.5L3.75 12l7.5-7.5')}
          {arrowBtn(pagePrev, prevDisabled, 'Previous', 'M15 19l-7-7 7-7')}
          <div className="text-sm text-gray-700 text-center tabular-nums whitespace-nowrap px-1 w-[6.5rem]">
            {windowLabel(granularity, anchor)}
          </div>
          {arrowBtn(pageNext, nextDisabled, 'Next', 'M9 5l7 7-7 7')}
          {arrowBtn(goLatest, nextDisabled, 'Latest', 'M5.25 4.5l7.5 7.5-7.5 7.5M12.75 4.5l7.5 7.5-7.5 7.5')}
        </div>
        <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
          {granularities.map((g) => (
            <button
              key={g}
              onClick={() => pickGranularity(g)}
              className={
                g === granularity
                  ? 'px-2 py-0.5 bg-action text-plum'
                  : 'px-2 py-0.5 text-gray-700 hover:bg-gray-50'
              }
            >
              {g}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChartTimeRangePicker;
